import { Injectable, inject } from '@angular/core';
import { Firestore, doc, docData, getDoc } from '@angular/fire/firestore';
import { HostnameIndex, TenantConfig } from '@models';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';

const HOSTNAME_INDEX_COLLECTION = 'hostname_index';
const TENANTS_COLLECTION = 'tenants';

/**
 * Resolves the current browser hostname to a tenant (pre-auth).
 *
 * On startup, reads `hostname_index/{hostname}` to find which tenant
 * owns the current domain. If found, loads the TenantConfig so the
 * login page can display tenant branding (logo, name, colors) before
 * the user authenticates.
 *
 * Standard hostnames (localhost, *.web.app, *.firebaseapp.com) are ignored
 * and resolve to null — the user will see the generic platform login.
 */
@Injectable({ providedIn: 'root' })
export class HostnameTenantService {
  private readonly firestoreDb = inject(Firestore);

  /**
   * The tenantId resolved from the current hostname.
   * null = not a custom domain (or domain not registered).
   */
  private resolvedTenantIdSubject = new BehaviorSubject<string | null>(null);
  public resolvedTenantId$ = this.resolvedTenantIdSubject.asObservable();

  /** Synchronous snapshot of the resolved tenantId. Available after app initialization. */
  get resolvedTenantId(): string | null {
    return this.resolvedTenantIdSubject.getValue();
  }

  /**
   * TenantConfig for the hostname-resolved tenant.
   * null = generic platform (no custom domain).
   * Only name, branding, and languages fields are guaranteed to be present
   * at the time the login page renders.
   */
  public tenantConfig$: Observable<TenantConfig | null> =
    this.resolvedTenantId$.pipe(
      switchMap((tenantId) => {
        if (!tenantId) return of(null);
        return docData(
          doc(this.firestoreDb, TENANTS_COLLECTION, tenantId),
        ).pipe(
          map((data) => (data ? (data as TenantConfig) : null)),
          catchError(() => of(null)),
        );
      }),
      shareReplay(1),
    );

  /**
   * Resolves the current browser hostname against the Firestore index.
   * Call this once from app.config.ts provideAppInitializer so the result
   * is available synchronously when the login component renders.
   *
   * @returns Promise that resolves when the lookup is complete.
   */
  async resolve(): Promise<void> {
    const hostname = this.getCurrentHostname();
    if (!hostname) return;

    try {
      const indexDoc = doc(
        this.firestoreDb,
        HOSTNAME_INDEX_COLLECTION,
        hostname,
      );
      const snap = await getDoc(indexDoc);
      if (snap.exists()) {
        const data = snap.data() as HostnameIndex;
        this.resolvedTenantIdSubject.next(data.tenantId);
      }
    } catch {
      // Silently ignore — the login page will fall back to generic branding
    }
  }

  /**
   * Updates the browser tab title and favicon to the hostname-resolved
   * tenant's branding. When the current hostname is a custom domain, the tab
   * shows the tenant's own name and logo instead of the generic "DMS Manager".
   *
   * No-op on generic platform hostnames (`tenantConfig$` stays null), so the
   * static `index.html` title/favicon remain untouched there.
   *
   * Fire-and-forget: subscribes to `tenantConfig$` (a shared read thanks to
   * `shareReplay`, so no extra Firestore request) and applies branding as soon
   * as the config is available. Call once at startup from an app initializer.
   */
  applyBrowserTabBranding(): void {
    this.tenantConfig$
      .pipe(filter((config): config is TenantConfig => config !== null))
      .subscribe((config) => {
        if (config.name) {
          document.title = config.name;
        }
        const iconUrl = config.branding?.iconUrl ?? config.branding?.logoUrl;
        if (iconUrl) {
          this.setFavicon(iconUrl);
        }
      });
  }

  /**
   * Points the browser tab favicon (and the iOS `apple-touch-icon`) at the
   * given image URL, creating the `<link>` elements if they are missing.
   * The static `type="image/x-icon"` is stripped so the browser honors the
   * new (typically PNG) image.
   */
  private setFavicon(url: string): void {
    if (typeof document === 'undefined') return;
    for (const rel of ['icon', 'apple-touch-icon']) {
      let link = document.head.querySelector<HTMLLinkElement>(
        `link[rel="${rel}"]`,
      );
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.removeAttribute('type');
      link.href = url;
    }
  }

  /**
   * Returns the current hostname if it looks like a custom domain.
   * Ignores localhost, Firebase-owned hostnames, and IP addresses.
   */
  private getCurrentHostname(): string | null {
    if (typeof window === 'undefined') return null;
    const host = window.location.hostname;

    const ignored = [
      'localhost',
      '127.0.0.1',
      '0.0.0.0',
      '.web.app',
      '.firebaseapp.com',
    ];
    if (ignored.some((suffix) => host === suffix || host.endsWith(suffix))) {
      return null;
    }

    return host;
  }
}
